import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import drinkImg from "../assets/drink-vending.jpg.asset.json";
import snackImg from "../assets/snack-vending.jpg.asset.json";
import comboImg from "../assets/combo-vending.png.asset.json";
import coffeeImg from "../assets/coffee-vending.jpg.asset.json";
import foodImg from "../assets/food-vending.png.asset.json";
import iceImg from "../assets/ice-vending.jpg.asset.json";
import aiSmartFridgeImg from "../assets/ai-smart-fridge.png.asset.json";
import lineup4Img from "../assets/lineup-4-machines.png.asset.json";
import bevmax4Img from "../assets/bevmax4-drink.jpg.asset.json";
import alpineCombiImg from "../assets/alpine-combi-food.jpg.asset.json";
import vendoGSnackImg from "../assets/vendo-g-snack.jpg.asset.json";
import amsComboImg from "../assets/ams-combo.jpg.asset.json";
import cashlessImg from "../assets/cashless-payments-only.png.asset.json";
import bevmaxLargeImg from "../assets/bevmax-drink-large.jpg.asset.json";
import freeVendingCtaImg from "../assets/free-vending-machines-cta.avif.asset.json";
import businessWomanImg from "../assets/satisfied-business-woman.png.asset.json";

const SITE_URL = "https://vendingmachinefree.com";
const TITLE = "Free Vending Machines — Drinks, Snacks, Coffee | USA";
const DESC =
  "Browse FREE vending machines for US businesses — drinks, snacks, combo, coffee, food, ice. Full-service install, restocking & service at no cost.";

type Machine = {
  slug: string;
  name: string;
  image: { url: string };
  tagline: string;
  bullets: string[];
  gallery?: { url: string; alt: string }[];
};

const MACHINES: Machine[] = [
  {
    slug: "ai-smart-fridge",
    name: "AI Smart Fridge Vending",
    image: aiSmartFridgeImg,
    tagline:
      "24H smart cooler with AI camera, tap-to-pay reader and real-time remote monitoring from your phone.",
    bullets: [
      "AI vision — grab-and-go, auto-charge on close",
      "Cashless tap, chip, mobile wallet & QR",
      "Live sales, stock & temp dashboard on iOS/Android",
    ],
    gallery: [
      { url: aiSmartFridgeImg.url, alt: "AI smart fridge vending machine with credit card reader" },
    ],
  },
  {
    slug: "drink",
    name: "Drink Vending Machines",
    image: drinkImg,
    tagline: "Cold sodas, sports drinks, juices and bottled water — refrigerated and ready 24/7.",
    bullets: [
      "Pepsi, Coca-Cola & national beverage brands",
      "Energy-efficient LED-lit display",
      "Cashless + card + mobile pay options",
    ],
    gallery: [
      { url: bevmax4Img.url, alt: "BevMax 4 wide glass-front drink vending machine" },
      { url: bevmaxLargeImg.url, alt: "Crane BevMax large bottle drink vending machine" },
    ],
  },
  {
    slug: "snack",
    name: "Snack Vending Machines",
    image: snackImg,
    tagline: "Chips, candy, cookies, protein bars and healthy options for any break room.",
    bullets: [
      "30+ selections per machine",
      "Guaranteed-delivery anti-theft trays",
      "Healthy + indulgent mix to fit your team",
    ],
    gallery: [
      { url: vendoGSnackImg.url, alt: "New Vendo G-Snack glass-front snack vending machine" },
    ],
  },
  {
    slug: "combo",
    name: "Combo Vending Machines",
    image: comboImg,
    tagline: "Snacks + drinks in one footprint — perfect for small or medium offices.",
    bullets: [
      "Snacks and beverages in a single unit",
      "Compact footprint for tight spaces",
      "Single point of service & restock",
    ],
    gallery: [
      { url: amsComboImg.url, alt: "AMS combo snack and drink vending machine with cashless reader" },
    ],
  },
  {
    slug: "coffee",
    name: "Coffee Vending Machines",
    image: coffeeImg,
    tagline: "Bean-to-cup espresso, lattes, cappuccinos and hot chocolate — barista quality.",
    bullets: [
      "Bean-to-cup brewing",
      "Touchscreen menu",
      "Optional free coffee for staff",
    ],
  },
  {
    slug: "food",
    name: "Food Vending Machines",
    image: foodImg,
    tagline: "Refrigerated fresh meals, sandwiches, salads and snacks for 24/7 staff.",
    bullets: [
      "Temperature-monitored refrigeration",
      "Daily/weekly fresh-food restocking",
      "Ideal for hospitals, plants & 24/7 sites",
    ],
    gallery: [
      { url: alpineCombiImg.url, alt: "Alpine combi refrigerated frozen food vending machine" },
    ],
  },
  {
    slug: "ice",
    name: "Ice Vending Machines",
    image: iceImg,
    tagline: "Commercial ice on-demand for hospitality, gas stations, marinas and outdoor venues.",
    bullets: [
      "High-output ice production",
      "Bag or bulk dispense",
      "Tough, weather-rated cabinet",
    ],
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "Are the vending machines really free?",
    a: "Yes — 100% free for qualified US businesses. We supply the machine, deliver, install, restock and service it at zero cost. You never buy, lease or finance the equipment, and there are no monthly fees. On higher-traffic sites we can add optional commission on sales.",
  },
  {
    q: "Who qualifies for a free vending machine?",
    a: "Most US locations with 25+ daily employees or visitors qualify — offices, warehouses, factories, gyms, auto shops, apartment complexes, medical facilities, schools, hotels, car dealerships and 24/7 sites. Smaller sites may still qualify for a combo or micro-market. Qualification takes about 24–48 hours after you submit the form.",
  },
  {
    q: "How fast can a machine be installed?",
    a: "Most qualified locations are surveyed within 3–5 business days and the machine is installed within 7–14 business days. Rush installs (48–72 hours) are possible in major US metros when equipment is on the truck.",
  },
  {
    q: "Do the machines accept credit cards and mobile pay?",
    a: "Yes. Every machine we place ships cashless-ready with tap-to-pay, chip, Apple Pay, Google Pay and QR through Nayax or Cantaloupe readers. Cash acceptance is optional — most modern sites opt for cashless only to eliminate coin jams and cash handling.",
  },
  {
    q: "Who handles restocking and repairs?",
    a: "We do. Restocking runs on a set schedule based on your machine's live sales data — typically weekly, sometimes twice a week for busy sites. All service calls, part replacements and firmware updates are included at no cost. Response time on down machines is typically under 24 hours in metros.",
  },
  {
    q: "What products go in the machine?",
    a: "You choose the product mix from a menu of 300+ items — national-brand sodas, waters, sports and energy drinks, chips, candy, cookies, protein bars, healthy snacks, sandwiches, salads, coffees and more. We adjust the planogram based on what your site actually buys.",
  },
  {
    q: "Do I get any revenue?",
    a: "On qualifying high-volume locations we offer a commission percentage of gross sales, paid monthly. Standard placements are free-of-charge with no commission but also no bills — many owners prefer the zero-hassle version. Both options are on the table when you apply.",
  },
  {
    q: "Which states do you cover?",
    a: "All 50 US states plus Washington DC. We have direct operator partners in every major metro — New York, Los Angeles, Chicago, Houston, Phoenix, Dallas, Atlanta, Miami, Seattle, Denver and more — and regional coverage everywhere in between.",
  },
];

const TOP_STATES: { name: string; slug: string }[] = [
  { name: "California", slug: "california" },
  { name: "Texas", slug: "texas" },
  { name: "Florida", slug: "florida" },
  { name: "New York", slug: "new-york" },
  { name: "Illinois", slug: "illinois" },
  { name: "Pennsylvania", slug: "pennsylvania" },
  { name: "Georgia", slug: "georgia" },
  { name: "Arizona", slug: "arizona" },
  { name: "North Carolina", slug: "north-carolina" },
  { name: "Ohio", slug: "ohio" },
  { name: "Washington", slug: "washington" },
  { name: "Colorado", slug: "colorado" },
  { name: "Michigan", slug: "michigan" },
  { name: "Virginia", slug: "virginia" },
  { name: "New Jersey", slug: "new-jersey" },
  { name: "Massachusetts", slug: "massachusetts" },
  { name: "Tennessee", slug: "tennessee" },
  { name: "Indiana", slug: "indiana" },
  { name: "Missouri", slug: "missouri" },
  { name: "Maryland", slug: "maryland" },
  { name: "Wisconsin", slug: "wisconsin" },
  { name: "Minnesota", slug: "minnesota" },
  { name: "Oregon", slug: "oregon" },
  { name: "Nevada", slug: "nevada" },
];


function track(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  try {
    const w = window as unknown as {
      gtag?: (...args: unknown[]) => void;
      dataLayer?: unknown[];
    };
    w.gtag?.("event", event, params);
    (w.dataLayer = w.dataLayer || []).push({ event, ...params });
  } catch {
    /* analytics best-effort */
  }
}

export const Route = createFileRoute("/vending-machines")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: `${SITE_URL}/vending-machines` },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${SITE_URL}${drinkImg.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: `${SITE_URL}${drinkImg.url}` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/vending-machines` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Free Vending Machine Types — USA",
          itemListElement: MACHINES.map((m, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: m.name,
              description: m.tagline,
              image: `${SITE_URL}${m.image.url}`,
              brand: { "@type": "Brand", name: "Vending Machine Free.com" },
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                areaServed: { "@type": "Country", name: "United States" },
              },
            },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            {
              "@type": "ListItem",
              position: 2,
              name: "Vending Machines",
              item: `${SITE_URL}/vending-machines`,
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Free Vending Machine Placement",
          name: "Free Vending Machines — Full-Service Placement",
          description:
            "Free vending machine placement for US businesses. We supply, deliver, install, restock and service drink, snack, combo, coffee, food, ice and AI smart-fridge vending machines at zero cost.",
          provider: {
            "@type": "Organization",
            name: "Vending Machine Free.com",
            url: SITE_URL,
            logo: `${SITE_URL}/logo.png`,
          },
          areaServed: { "@type": "Country", name: "United States" },
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Vending Machine Types",
            itemListElement: MACHINES.map((m) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Product", name: m.name, description: m.tagline },
              price: "0",
              priceCurrency: "USD",
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `${SITE_URL}#business`,
          name: "Vending Machine Free.com",
          url: SITE_URL,
          image: `${SITE_URL}${drinkImg.url}`,
          telephone: "+1-800-000-0000",
          priceRange: "Free",
          areaServed: [
            { "@type": "Country", name: "United States" },
            ...TOP_STATES.map((s) => ({ "@type": "State", name: s.name })),
          ],
          address: {
            "@type": "PostalAddress",
            addressCountry: "US",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),

  component: VendingMachinesPage,
});

function VendingMachinesPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/90 backdrop-blur sticky top-0 z-40">
        <div className="container-x flex h-16 md:h-20 items-center justify-between gap-3">
          <Link to="/" className="font-display text-lg font-bold uppercase">
            Vending Machine Free.com
          </Link>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground hover:opacity-95"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <section className="text-steel-foreground" style={{ background: "var(--gradient-hero)" }}>
        <div className="container-x py-12 md:py-16 grid gap-10 md:grid-cols-2 items-center">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              100% Free · USA Nationwide
            </div>
            <h1 className="mt-4 font-display text-3xl md:text-5xl uppercase leading-tight">
              Free Vending Machines — Every Type, One Supplier
            </h1>
            <p className="mt-4 text-white/85 max-w-xl mx-auto md:mx-0">
              Drinks, snacks, combo, coffee, food and ice — we supply, install, restock and service
              every machine on this page at no cost for qualified US locations.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <a
                href="#lead-form"
                onClick={() => {
                  track("cta_click", {
                    cta_id: "hero_get_free_machine",
                    cta_location: "vending-machines-hero",
                    cta_text: "Get My Free Machine",
                  });
                  track("select_content", {
                    content_type: "cta",
                    content_id: "hero_get_free_machine",
                  });
                }}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-safety text-safety-foreground font-bold px-5 py-3 uppercase tracking-wide hover:brightness-110 transition"
              >
                Get My Free Machine <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to="/vending-machine-cost"
                onClick={() =>
                  track("cta_click", {
                    cta_id: "hero_cost_breakdown",
                    cta_location: "vending-machines-hero",
                  })
                }
                className="inline-flex items-center justify-center rounded-md border border-white/30 bg-white/5 text-white font-semibold px-5 py-3 uppercase tracking-wide hover:bg-white/10 transition"
              >
                See Cost Breakdown
              </Link>
            </div>

          </div>
          <div className="rounded-xl overflow-hidden bg-white/5 border border-white/15 shadow-2xl">
            <img
              src={freeVendingCtaImg.url}
              alt="Free vending machines for workplaces and businesses — all 6 types: drinks, snacks, combo, coffee, food and ice"
              width={1200}
              height={900}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>


      <section className="container-x pt-10 md:pt-14">
        <figure className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
          <img
            src={lineup4Img.url}
            alt="Free vending machine fleet lineup — snack, large combo, drinks and coffee machines available across the USA"
            width={1600}
            height={734}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          <figcaption className="px-4 py-3 text-xs text-muted-foreground text-center">
            Our current placement fleet — snack · large combo · drinks · coffee. Free install &
            service, USA nationwide.
          </figcaption>
        </figure>
      </section>

      <section className="container-x py-12 md:py-16">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {MACHINES.map((m) => (
            <article
              key={m.slug}
              id={m.slug}
              className="group rounded-lg border border-border bg-card overflow-hidden flex flex-col"
            >
              <div className="aspect-[4/3] bg-secondary overflow-hidden">
                <img
                  src={m.image.url}
                  alt={`${m.name} — free placement across the USA by Vending Machine Free.com`}
                  width={1024}
                  height={1024}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h2 className="font-display text-xl uppercase">{m.name}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{m.tagline}</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {m.bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {m.gallery && m.gallery.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {m.gallery.map((g) => (
                      <div
                        key={g.url}
                        className="aspect-square bg-secondary rounded-md overflow-hidden border border-border"
                      >
                        <img
                          src={g.url}
                          alt={g.alt}
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <nav
                  aria-label={`${m.name} — top state coverage`}
                  className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground"
                >
                  <span className="font-semibold text-foreground/80 mr-1">Serving:</span>
                  {TOP_STATES.slice(0, 6).map((s, i) => (
                    <span key={s.slug}>
                      <Link
                        to="/states/$state"
                        params={{ state: s.slug }}
                        className="hover:text-safety hover:underline"
                        onClick={() =>
                          track("internal_link_click", {
                            link_type: "machine_to_state",
                            machine: m.slug,
                            state: s.slug,
                          })
                        }
                      >
                        {s.name}
                      </Link>
                      {i < 5 ? " · " : ""}
                    </span>
                  ))}
                </nav>
                <a
                  href="#lead-form"
                  onClick={() =>
                    track("cta_click", {
                      cta_id: "machine_card_apply",
                      cta_location: "vending-machines-grid",
                      machine: m.slug,
                    })
                  }
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-steel px-4 py-2.5 text-sm font-semibold text-steel-foreground hover:opacity-95"
                >
                  Get this machine free <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </article>
          ))}
        </div>

        <nav
          aria-label="Top state coverage"
          className="mt-10 rounded-lg border border-border bg-secondary/40 p-5"
        >
          <h3 className="font-display text-sm uppercase tracking-wide text-muted-foreground">
            Free vending machines — top states we serve
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {TOP_STATES.map((s) => (
              <Link
                key={s.slug}
                to="/states/$state"
                params={{ state: s.slug }}
                onClick={() =>
                  track("internal_link_click", {
                    link_type: "top_states_strip",
                    state: s.slug,
                  })
                }
                className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-sm hover:border-safety hover:text-safety transition"
              >
                {s.name}
              </Link>
            ))}
            <Link
              to="/coverage"
              className="inline-flex items-center rounded-full bg-steel text-steel-foreground px-3 py-1.5 text-sm font-semibold hover:opacity-95"
            >
              See all 50 states →
            </Link>
          </div>
        </nav>
      </section>


      <section className="border-t border-border bg-background">
        <div className="container-x py-12 md:py-16 grid gap-8 md:grid-cols-2 items-center">
          <div className="rounded-xl overflow-hidden border border-border bg-white p-6 flex items-center justify-center">
            <img
              src={cashlessImg.url}
              alt="Cashless payment only — tap, chip, mobile wallet accepted on every vending machine"
              width={640}
              height={480}
              loading="lazy"
              decoding="async"
              className="max-h-64 w-auto object-contain"
            />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-safety/10 text-safety px-3 py-1 text-xs font-bold uppercase tracking-wider">
              100% Cashless · Zero Coin Jams
            </div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl uppercase">
              Every Machine Ships Cashless-Ready
            </h2>
            <p className="mt-3 text-muted-foreground">
              Tap-to-pay, chip, Apple Pay, Google Pay and QR are standard on our AI smart fridges,
              drink towers, snack, combo and coffee machines. No coin jams, no cash handling for
              your staff, and full remote sales reporting in real time.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>Nayax / Cantaloupe cashless readers included</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>Live inventory & sales dashboard on your phone</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>PCI-compliant — we handle all payment processing</span></li>
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-background">
        <div className="container-x py-12 md:py-16 grid gap-10 md:grid-cols-2 items-center">
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-steel/10 text-steel px-3 py-1 text-xs font-bold uppercase tracking-wider">
              Zero Cost · Zero Hassle
            </div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl uppercase">
              Business Owners Love the $0 Model
            </h2>
            <p className="mt-3 text-muted-foreground">
              We install, stock and service every machine at our expense. You get a modern amenity
              staff and visitors actually use — with revenue-share options on higher-traffic sites —
              and none of the operational overhead.
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>$0 machine cost</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>$0 install & delivery</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>$0 restock & service</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>Optional commission</span></li>
            </ul>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md bg-safety text-safety-foreground font-bold px-5 py-3 uppercase tracking-wide hover:brightness-110 transition"
              >
                Qualify My Location <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="order-1 md:order-2 flex justify-center">
            <img
              src={businessWomanImg.url}
              alt="Satisfied business owner with free vending machine service — automated revenue and zero maintenance"
              width={970}
              height={1040}
              loading="lazy"
              decoding="async"
              className="w-full max-w-md h-auto object-contain"
            />
          </div>
        </div>
      </section>

      <section id="lead-form" className="border-t border-border bg-secondary/40 scroll-mt-24">
        <div className="container-x py-12 md:py-16 grid gap-10 md:grid-cols-5 items-start">
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-safety/10 text-safety px-3 py-1 text-xs font-bold uppercase tracking-wider">
              Free Placement · 24–48 Hr Qualification
            </div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl uppercase">
              Get Your Free Vending Machine
            </h2>
            <p className="mt-3 text-muted-foreground">
              Tell us about your US location and we'll match you with a local operator who supplies,
              installs and services your machine at zero cost. Most sites qualify in under 48 hours.
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>No credit check · no lease · no fees</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>Direct match to a vetted local operator</span></li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-safety shrink-0" /><span>Response typically within 4–6 business hours</span></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <QuickApplyForm />
          </div>
        </div>
      </section>

      <section
        id="faq"
        className="border-t border-border bg-background scroll-mt-24"
        aria-labelledby="faq-heading"
      >
        <div className="container-x py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-steel/10 text-steel px-3 py-1 text-xs font-bold uppercase tracking-wider">
              AEO · Answer Engine Optimized
            </div>
            <h2 id="faq-heading" className="mt-3 font-display text-2xl md:text-3xl uppercase">
              Frequently Asked Questions
            </h2>
            <p className="mt-3 text-muted-foreground">
              Clear answers about pricing (free), eligibility, cashless payments, installation
              timelines and restocking for US businesses.
            </p>
          </div>
          <div className="mt-8 max-w-3xl mx-auto divide-y divide-border rounded-lg border border-border bg-card">
            {FAQS.map((f, i) => (
              <details key={f.q} className="group p-5 open:bg-secondary/40" open={i === 0}>
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4 font-semibold text-foreground">
                  <span>{f.q}</span>
                  <span className="mt-1 shrink-0 rounded-full border border-border w-6 h-6 grid place-items-center text-xs group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section
        className="border-t border-border bg-secondary/30"
        aria-labelledby="seo-heading"
      >
        <div className="container-x py-12 md:py-16 max-w-4xl mx-auto">
          <h2 id="seo-heading" className="font-display text-2xl md:text-3xl uppercase">
            Free Vending Machines in the USA — Types, Use Cases & Nationwide Service
          </h2>

          <h3 className="mt-6 font-display text-xl uppercase text-steel">
            How free vending machine placement works
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            A free vending machine program pairs your US business with a full-service vending
            operator who owns the equipment. The operator supplies the machine, pays for delivery
            and installation, stocks it with product, collects the revenue, and handles every
            service call. You provide the floor space and reliable power (a standard 110V outlet
            for most machines). There is no purchase, no lease, no monthly service contract, and
            no maintenance invoice — the operator earns from product sales while you gain a modern
            amenity for employees, tenants, guests or customers. On higher-traffic locations
            (typically 100+ daily users), operators can offer a commission percentage of gross
            sales, paid monthly. Standard placements are $0 with no commission and no bills, which
            most owners prefer for the pure zero-hassle model.
          </p>

          <h3 className="mt-6 font-display text-xl uppercase text-steel">
            The six vending machine types we place
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Drink vending machines</strong> hold 300–600
            refrigerated bottles and cans across sodas, waters, sports drinks, energy drinks and
            juices — the workhorse of gyms, warehouses and 24/7 sites.{" "}
            <strong className="text-foreground">Snack vending machines</strong> stock 30–45
            selections of chips, candy, cookies, granola bars, protein bars and healthy options,
            with guaranteed-delivery anti-theft trays so paid product always drops.{" "}
            <strong className="text-foreground">Combo vending machines</strong> put snacks and
            drinks into a single cabinet — ideal for small and medium offices, auto shops, dental
            practices and any site where floor space is tight but staff still want both categories.{" "}
            <strong className="text-foreground">Coffee vending machines</strong> use bean-to-cup
            brewing to serve espresso, cappuccino, latte, americano and hot chocolate at barista
            quality, with an optional free-coffee mode for staff.{" "}
            <strong className="text-foreground">Food vending machines</strong> are refrigerated
            units carrying fresh sandwiches, salads, wraps, yogurts and full meals with daily or
            weekly restocks — essential for hospitals, manufacturing plants and other 24/7 sites
            without an on-site cafeteria.{" "}
            <strong className="text-foreground">Ice vending machines</strong> produce commercial
            ice on demand for hospitality, gas stations, marinas and outdoor venues.{" "}
            <strong className="text-foreground">AI smart-fridge vending</strong> uses computer
            vision — customers tap a card, open the door, grab what they want, and the fridge
            auto-charges when it closes. Every machine ships cashless-ready with tap-to-pay, chip,
            Apple Pay, Google Pay and QR through Nayax or Cantaloupe readers.
          </p>

          <h3 className="mt-6 font-display text-xl uppercase text-steel">
            Who qualifies — best-fit locations
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            The single biggest qualifier is <strong className="text-foreground">daily foot
            traffic</strong>. Most operators want to see 25+ employees or visitors per day for a
            single machine, and 75+ for a combo or bank of machines. Beyond headcount, the highest
            performing sites we place are corporate offices, coworking spaces, warehouses and
            distribution centers, manufacturing plants, auto dealerships, auto body and repair
            shops, gyms and fitness studios, apartment and condo buildings, hotels and hospitality
            venues, hospitals and medical office buildings, schools and universities, government
            and military facilities, airports, transit hubs and 24/7 operations. Smaller sites can
            still qualify for a compact combo machine or a micro-market. If you're not sure whether
            your site qualifies, submit the form above — a qualification decision usually comes
            back within 24 to 48 hours.
          </p>

          <h3 className="mt-6 font-display text-xl uppercase text-steel">
            Nationwide USA service — every state, every metro
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            We coordinate free vending machine placement across all 50 US states plus Washington
            DC through a national network of vetted local operators. That includes every major
            metro — <Link to="/states/$state" params={{ state: "new-york" }} className="text-safety hover:underline">New York</Link>,{" "}
            <Link to="/states/$state" params={{ state: "california" }} className="text-safety hover:underline">Los Angeles and San Francisco</Link>,{" "}
            <Link to="/states/$state" params={{ state: "illinois" }} className="text-safety hover:underline">Chicago</Link>,{" "}
            <Link to="/states/$state" params={{ state: "texas" }} className="text-safety hover:underline">Houston, Dallas and Austin</Link>,{" "}
            <Link to="/states/$state" params={{ state: "arizona" }} className="text-safety hover:underline">Phoenix</Link>,{" "}
            <Link to="/states/$state" params={{ state: "florida" }} className="text-safety hover:underline">Miami, Tampa and Orlando</Link>,{" "}
            <Link to="/states/$state" params={{ state: "georgia" }} className="text-safety hover:underline">Atlanta</Link>,{" "}
            <Link to="/states/$state" params={{ state: "washington" }} className="text-safety hover:underline">Seattle</Link>,{" "}
            <Link to="/states/$state" params={{ state: "colorado" }} className="text-safety hover:underline">Denver</Link> and{" "}
            <Link to="/states/$state" params={{ state: "ohio" }} className="text-safety hover:underline">Columbus and Cleveland</Link>{" "}
            — plus regional coverage everywhere in between. Because we route your application to a
            local operator in your ZIP code, installation, restocking and service happen on a local
            schedule with local response times, not from a national warehouse thousands of miles
            away.
          </p>

          <h3 className="mt-6 font-display text-xl uppercase text-steel">
            Installation, restocking and service — what happens after you apply
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Once your application is qualified, a local placement rep contacts you within a few
            business hours to confirm the site, walk through machine options, and schedule a site
            survey. The survey verifies floor space (typically 3 ft wide by 3 ft deep for a single
            machine), power access, delivery clearance and product preferences. Most machines are
            installed within 7 to 14 business days of the survey; rush installs of 48 to 72 hours
            are possible in major metros when equipment is already on the truck. Restocking runs
            on a data-driven schedule — the machine's cashless reader reports live sales, and the
            route driver arrives before it runs low, typically weekly for most sites and twice a
            week for busy locations. Every service call, part replacement and firmware update is
            included at no cost; typical response time on a down machine in a metro is under 24
            hours. Product mix is chosen from a 300+ item menu and adjusted based on what your
            site actually buys, so slow movers rotate out and best-sellers stay in stock.
          </p>

          <h3 className="mt-6 font-display text-xl uppercase text-steel">
            Ready to get started?
          </h3>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            The fastest path is the qualification form above — it takes about two minutes and asks
            for the basics: business name, address, daily headcount, hours and which{" "}
            <Link to="/vending-machines" className="text-safety hover:underline">
              vending machine types
            </Link>{" "}
            you're interested in. If your site qualifies, a local operator will be in touch within
            a few business hours. In the meantime you can browse{" "}
            <Link to="/coverage" className="text-safety hover:underline">
              our state coverage map
            </Link>
            , check the{" "}
            <Link to="/vending-machine-cost" className="text-safety hover:underline">
              cost breakdown
            </Link>{" "}
            (spoiler: it's $0), or read more about{" "}
            <Link to="/about" className="text-safety hover:underline">
              20+ years of nationwide placement experience
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="bg-secondary border-t border-border">



        <div className="container-x py-12 md:py-16 text-center">
          <h2 className="font-display text-2xl md:text-3xl uppercase">
            Not sure which machine fits your site?
          </h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Tell us about your location and our placement team will recommend the right mix — we
            try to respond in less than 4–6 hrs.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-safety px-5 py-3 font-semibold text-safety-foreground"
            >
              Apply for Free Placement <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-input bg-background px-5 py-3 font-semibold hover:bg-accent"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

type FormStatus = "idle" | "submitting" | "success" | "error";

const US_STATES_SHORT: { name: string; code: string }[] = [
  { name: "Alabama", code: "AL" }, { name: "Alaska", code: "AK" }, { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" }, { name: "California", code: "CA" }, { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" }, { name: "Delaware", code: "DE" }, { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" }, { name: "Hawaii", code: "HI" }, { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" }, { name: "Indiana", code: "IN" }, { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" }, { name: "Kentucky", code: "KY" }, { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" }, { name: "Maryland", code: "MD" }, { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" }, { name: "Minnesota", code: "MN" }, { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" }, { name: "Montana", code: "MT" }, { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" }, { name: "New Hampshire", code: "NH" }, { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" }, { name: "New York", code: "NY" }, { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" }, { name: "Ohio", code: "OH" }, { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" }, { name: "Pennsylvania", code: "PA" }, { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" }, { name: "South Dakota", code: "SD" }, { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" }, { name: "Utah", code: "UT" }, { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" }, { name: "Washington", code: "WA" }, { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" }, { name: "Wyoming", code: "WY" }, { name: "Washington DC", code: "DC" },
];

function QuickApplyForm() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [startedAt] = useState(() => Date.now());

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "submitting") return;
    setError(null);

    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      full_name: String(fd.get("full_name") ?? "").trim(),
      business_name: String(fd.get("business_name") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      phone: String(fd.get("phone") ?? "").trim(),
      street: String(fd.get("street") ?? "").trim(),
      city: String(fd.get("city") ?? "").trim(),
      state: String(fd.get("state") ?? "").trim(),
      zip: String(fd.get("zip") ?? "").trim(),
      employees: Number(fd.get("employees") ?? 0) || undefined,
      machine_type: String(fd.get("machine_type") ?? "") || undefined,
      location_notes: String(fd.get("location_notes") ?? "").trim(),
      website: String(fd.get("website") ?? ""),
      form_started_at: startedAt,
    };

    track("form_submit_attempt", { form_id: "vending_machines_quick_apply" });
    setStatus("submitting");

    try {
      const res = await fetch("/api/public/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        const msg = data.error ?? `Submission failed (${res.status})`;
        track("form_validation_error", { form_id: "vending_machines_quick_apply", error: msg });
        setError(msg);
        setStatus("error");
        return;
      }
      track("generate_lead", {
        form_id: "vending_machines_quick_apply",
        currency: "USD",
        value: 0,
        machine_type: payload.machine_type ?? "unspecified",
        state: payload.state,
      });
      track("form_submit_success", { form_id: "vending_machines_quick_apply" });
      setStatus("success");
      form.reset();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Network error";
      track("form_validation_error", { form_id: "vending_machines_quick_apply", error: msg });
      setError(msg);
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-xl border border-safety/40 bg-safety/5 p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-safety mx-auto" />
        <h3 className="mt-3 font-display text-xl uppercase">Application received</h3>
        <p className="mt-2 text-muted-foreground">
          Thanks! A local placement rep will email you within 4–6 business hours to confirm
          qualification and schedule your site survey.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-safety/40";

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-4"
      noValidate
    >
      <div className="hidden" aria-hidden="true">
        <label>
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-semibold">Full name *</span>
          <input required name="full_name" autoComplete="name" className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block text-sm">
          <span className="font-semibold">Business name *</span>
          <input required name="business_name" autoComplete="organization" className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block text-sm">
          <span className="font-semibold">Email *</span>
          <input required type="email" name="email" autoComplete="email" className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block text-sm">
          <span className="font-semibold">Phone *</span>
          <input required type="tel" name="phone" autoComplete="tel" className={`mt-1 ${inputCls}`} />
        </label>
      </div>

      <label className="block text-sm">
        <span className="font-semibold">Street address *</span>
        <input required name="street" autoComplete="street-address" className={`mt-1 ${inputCls}`} />
      </label>

      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block text-sm sm:col-span-1">
          <span className="font-semibold">City *</span>
          <input required name="city" autoComplete="address-level2" className={`mt-1 ${inputCls}`} />
        </label>
        <label className="block text-sm sm:col-span-1">
          <span className="font-semibold">State *</span>
          <select required name="state" defaultValue="" className={`mt-1 ${inputCls}`}>
            <option value="" disabled>Select…</option>
            {US_STATES_SHORT.map((s) => (
              <option key={s.code} value={s.name}>{s.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm sm:col-span-1">
          <span className="font-semibold">ZIP *</span>
          <input required name="zip" inputMode="numeric" autoComplete="postal-code" className={`mt-1 ${inputCls}`} />
        </label>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-semibold">Daily employees / visitors</span>
          <input type="number" min={0} name="employees" className={`mt-1 ${inputCls}`} placeholder="e.g. 75" />
        </label>
        <label className="block text-sm">
          <span className="font-semibold">Machine type of interest</span>
          <select name="machine_type" defaultValue="" className={`mt-1 ${inputCls}`}>
            <option value="">No preference</option>
            <option value="Drink">Drink</option>
            <option value="Snack">Snack</option>
            <option value="Combo">Combo (snack + drink)</option>
            <option value="Coffee">Coffee</option>
            <option value="Food">Food (fresh / frozen)</option>
            <option value="Ice">Ice</option>
          </select>
        </label>
      </div>

      <label className="block text-sm">
        <span className="font-semibold">Location notes (optional)</span>
        <textarea
          name="location_notes"
          rows={3}
          className={`mt-1 ${inputCls}`}
          placeholder="Hours of operation, break room location, existing machines, etc."
        />
      </label>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2 text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-safety text-safety-foreground font-bold px-5 py-3 uppercase tracking-wide hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
          </>
        ) : (
          <>
            Get My Free Machine <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>

      <p className="text-xs text-muted-foreground text-center">
        We reply within 4–6 business hours. No spam — your info goes only to a vetted local
        vending operator.
      </p>
    </form>
  );
}

